/**
 * Microsite Slice
 * Manages single microsite data (for viewing/editing)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { MicrositeState } from '../types';
import type { MicrositeDetailResponse } from '../../types/api.types';
import apiClient from '../../utils/api/ApiClient';
import { API_ENDPOINTS } from '../../constants/api.constants';

// Initial state
const initialState: MicrositeState = {
    currentMicrosite: null,
    loading: 'idle',
    error: null,
};

// ============================================
// Async Thunks
// ============================================

/**
 * Fetch microsite by ID (for editing)
 */
export const fetchMicrositeById = createAsyncThunk(
    'microsite/fetchById',
    async (micrositeID: string) => {
        const endpoint = API_ENDPOINTS.MICROSITE.ADMIN_DATA(micrositeID);

        const response = await apiClient.get<MicrositeDetailResponse>(endpoint);

        return response.data;
    }
);

// ============================================
// Slice
// ============================================

const micrositeSlice = createSlice({
    name: 'microsite',
    initialState,
    reducers: {
        /**
         * Clear current microsite
         */
        clearCurrentMicrosite: (state) => {
            state.currentMicrosite = null;
            state.error = null;
        },

        /**
         * Clear error
         */
        clearMicrositeError: (state) => {
            state.error = null;
        },

        /**
         * Reset microsite state
         */
        resetMicrositeState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // Fetch microsite by ID
            .addCase(fetchMicrositeById.pending, (state) => {
                state.loading = 'loading';
                state.error = null;
            })
            .addCase(fetchMicrositeById.fulfilled, (state, action) => {
                state.loading = 'success';
                state.currentMicrosite = action.payload;
                state.error = null;
            })
            .addCase(fetchMicrositeById.rejected, (state, action) => {
                state.loading = 'error';
                state.error = action.error.message || 'Failed to fetch microsite';
            });
    },
});

// ============================================
// Actions
// ============================================

export const { clearCurrentMicrosite, clearMicrositeError, resetMicrositeState } =
    micrositeSlice.actions;

// ============================================
// Selectors (Custom Hooks)
// ============================================

/**
 * Get current microsite
 */
export const useCurrentMicrosite = () => {
    return useSelector((state: RootState) => state.microsite.currentMicrosite);
};

/**
 * Get loading state
 */
export const useMicrositeLoading = () => {
    return useSelector((state: RootState) => state.microsite.loading);
};

/**
 * Get error
 */
export const useMicrositeError = () => {
    return useSelector((state: RootState) => state.microsite.error);
};

/**
 * Get entire microsite state
 */
export const useMicrositeState = () => {
    return useSelector((state: RootState) => state.microsite);
};

// ============================================
// Reducer
// ============================================

export default micrositeSlice.reducer;
